/* Suchen, Auswählen und Ersetzen (Strg+F): Texturen auf Brushes/Flächen, Werte von Entities. */
import { useState } from 'react';
import Cmd from '../../model/cmds.ts';
import Find from '../../model/find.ts';
import type { FaceRef } from '../../model/find.ts';
import type { Doc, Sel } from '../../model/types.ts';
import { Dialog, DialogBody, DialogFoot } from '../dialog.tsx';
import { useT } from '../hooks.ts';

/** Eingaben, bleiben beim erneuten Öffnen erhalten */
export interface FindState { tex: string; repl: string; onlySel: boolean; cls: string; key: string; val: string; kvRepl: string; msg: string }
export const newFindState = (): FindState => ({ tex: '', repl: '', onlySel: false, cls: '', key: '', val: '', kvRepl: '', msg: '' });

export interface FindApp {
  doc: Doc; sel: Sel;
  viewDoc(): Doc; hiddenSet(): Set<string>;
  setSel(sel: Sel): void; setFaceSel(list: FaceRef[]): void; commit(doc: Doc, label: string): void; status(msg: string): void; fit(selection: boolean): void;
}

export function FindDialog(props: { app: FindApp; state: FindState; onClose: () => void }) {
  const t = useT();
  const { app, state: F } = props;
  const [, setTick] = useState(0);
  const set = (k: keyof FindState) => (e: { target: { value: string } }) => { (F[k] as string) = e.target.value; setTick((n) => n + 1); };
  const say = (m: string) => { F.msg = m; app.status(m); setTick((n) => n + 1); };
  const scope = () => (F.onlySel && app.sel.b.size ? new Set(app.sel.b) : null);
  const visibleSel = (ids: Iterable<string>, kind: 'b' | 'e', ns: Sel, hid: Set<string>) => { for (const id of ids) if (!hid.has(id)) ns[kind].add(id); };
  const inp = (k: keyof FindState, ph: string, list?: string) => <input type="text" className="inp" value={F[k] as string} placeholder={ph} aria-label={ph} list={list} autoComplete="off" spellCheck={false} onChange={set(k)} />;

  const selBrushes = () => {
    const ids = Find.brushesByTex(app.viewDoc(), F.tex, scope()), ns = Cmd.emptySel();
    visibleSel(ids, 'b', ns, app.hiddenSet()); app.setSel(ns);
    say(ids.size ? t('find.brushes_selected', { count: ns.b.size }) : t('find.no_brushes'));
  };
  const selFaces = () => {
    const fl = Find.facesByTex(app.viewDoc(), F.tex, scope());
    if (fl.length) { app.setFaceSel(fl); say(t('find.faces_selected', { count: fl.length })); } else say(t('find.no_faces'));
  };
  const replaceTex = () => {
    if (!F.tex.trim() || !F.repl.trim()) { say(t('find.need_both')); return; }
    const r = Find.replaceTex(app.doc, F.tex, F.repl, scope());
    if (!r.faces) { say(t('find.nothing')); return; }
    app.commit(r.doc, t('cmd.replace_tex')); say(t('find.replaced', { faces: r.faces, brushes: r.brushes }));
  };
  const selEnts = () => {
    const r = Find.entsBy(app.viewDoc(), { cls: F.cls, key: F.key, val: F.val }), hid = app.hiddenSet(), ns = Cmd.emptySel();
    visibleSel(r.b, 'b', ns, hid); visibleSel(r.e, 'e', ns, hid);
    app.setSel(ns); say(r.ents.length ? t('find.ents_selected', { count: r.ents.length }) : t('find.no_ents'));
  };
  const replaceKV = () => {
    if (!F.key.trim()) { say(t('find.need_field')); return; }
    const r = Find.replaceKV(app.doc, { cls: F.cls, key: F.key, val: F.val }, F.kvRepl);
    if (!r.n) { say(t('find.no_ents_field')); return; }
    app.commit(r.doc, t('cmd.replace_kv')); say(t('find.ents_changed', { count: r.n }));
  };

  return (
    <Dialog title={t('find.title')} onClose={props.onClose}>
      <DialogBody>
        <h3 className="lbl-s">{t('find.textures')}</h3>
        <div className="fields">
          <label>{t('find.search')}</label>{inp('tex', t('find.ph_tex'), 'dl-tex')}
          <label>{t('find.replace_with')}</label>{inp('repl', t('find.ph_newtex'), 'dl-tex')}
        </div>
        <label className="chk"><input type="checkbox" checked={F.onlySel} onChange={(e) => { F.onlySel = e.target.checked; setTick((n) => n + 1); }} />{t('find.only_sel')}</label>
        <div className="row">
          <button className="btn" onClick={selBrushes}>{t('find.select_brushes')}</button>
          <button className="btn" onClick={selFaces}>{t('find.select_faces')}</button>
          <button className="btn primary" onClick={replaceTex}>{t('find.replace')}</button>
        </div>
        <h3 className="lbl-s">{t('find.entities')}</h3>
        <div className="fields">
          <label>{t('ui.opts.class')}</label>{inp('cls', t('find.ph_cls'), 'dl-cls')}
          <label>{t('find.field')}</label>{inp('key', t('find.ph_key'))}
          <label>{t('find.value')}</label>{inp('val', t('find.ph_val'))}
          <label>{t('find.new_value')}</label>{inp('kvRepl', t('find.ph_newval'))}
        </div>
        <div className="row">
          <button className="btn" onClick={selEnts}>{t('find.select_ents')}</button>
          <button className="btn primary" title={t('find.replace_value_tip')} onClick={replaceKV}>{t('find.replace_value')}</button>
        </div>
        <p className="small" role="status">{F.msg || t('find.pattern_hint')}</p>
      </DialogBody>
      <DialogFoot>
        <button className="btn" onClick={() => app.fit(false)}>{t('find.show_sel')}</button>
        <button className="btn primary" onClick={props.onClose}>{t('ui.close')}</button>
      </DialogFoot>
    </Dialog>
  );
}
